import { useState, useCallback } from 'react';
import { useCreateAttributeBindingMutation, useSetTaxonomyAttributeValueMutation } from '../../api/cropsApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { TaxonomyScope } from '../../models/Crop';

interface UseAttributeBindingProps {
  saveId: string;
  onSuccess?: () => Promise<void>;
}

export const useAttributeBinding = ({ saveId, onSuccess }: UseAttributeBindingProps) => {
  const [attributeToBind, setAttributeToBind] = useState<string>();
  const [bindingValue, setBindingValue] = useState<{ choiceId?: string | null; customValue?: string | null }>({});

  const [createAttributeBinding, { isLoading: creatingBinding }] = useCreateAttributeBindingMutation();
  const [setTaxonomyAttributeValue] = useSetTaxonomyAttributeValueMutation();

  const handleAddAttributeToScope = useCallback(
    async (scope: TaxonomyScope, scopeId: number | string, selectedAttribute: any) => {
      if (!scope || !attributeToBind) {
        toastError('Выберите атрибут для добавления');
        return;
      }
      if (!saveId) {
        toastError('Не выбран активный сейв');
        return;
      }

      try {
        const numericAttributeId = Number(attributeToBind);
        const bindingPayload = {
          attributeId: Number.isNaN(numericAttributeId) ? attributeToBind : numericAttributeId,
          scope,
          scopeId,
          saveId,
        };
        await createAttributeBinding(bindingPayload).unwrap();

        // Если указано значение по умолчанию, устанавливаем его
        const hasOptions = selectedAttribute?.options && selectedAttribute.options.length > 0;
        const hasValue = hasOptions
          ? bindingValue.choiceId != null
          : bindingValue.customValue != null && bindingValue.customValue.trim() !== '';

        if (hasValue) {
          const valuePayload: any = {
            scope,
            scopeId,
            attributeId: numericAttributeId,
            saveId,
          };

          if (hasOptions) {
            const choiceId = bindingValue.choiceId;
            valuePayload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
            valuePayload.customValue = null;
          } else {
            valuePayload.choiceId = null;
            valuePayload.customValue = bindingValue.customValue?.trim() || null;
          }

          await setTaxonomyAttributeValue(valuePayload).unwrap();
        }

        toastSuccess('Атрибут привязан к выбранному уровню');
        setAttributeToBind(undefined);
        setBindingValue({});

        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        toastError(error);
      }
    },
    [attributeToBind, bindingValue, saveId, createAttributeBinding, setTaxonomyAttributeValue, onSuccess],
  );

  const resetBinding = useCallback(() => {
    setAttributeToBind(undefined);
    setBindingValue({});
  }, []);

  return {
    attributeToBind,
    setAttributeToBind,
    bindingValue,
    setBindingValue,
    creatingBinding,
    handleAddAttributeToScope,
    resetBinding,
  };
};
